import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { DictionaryEntryType, DictionaryEntry } from 'types/dictionary'
import { LoadingStatus } from 'types/loading'

import { getDictionaryLinkedEntriesRequest } from 'store/dictionary/dictionary.actions'
import { useDictionaryByEntry } from 'store/dictionary/dictionary.hooks'
import { selectToken } from 'store/users/users.selectors'

import NegativeStatus from 'components/molecules/NegativeStatus'
import GrayText from 'components/atoms/GrayText'

type FoundNegativesLegacy = { groups?: string[] }
type FoundNegativesFromApi = Array<{ year?: string; group?: string }>
type PersonWithFoundNegatives = DictionaryEntry & { foundNegatives?: FoundNegativesLegacy | FoundNegativesFromApi }

interface DictionaryFoundNegativesProps {
  person: { name: string; birthday: string }
  /** Не показывать блок, если в справочнике нет непустых groups */
  hideEmpty?: boolean
}

const toUtcDictionaryBirthdayString = (yyyy: number, mm: number, dd: number) => {
  // Matches how backend stores JS Date.toString() in UTC
  const d = new Date(Date.UTC(yyyy, mm - 1, dd, 0, 0, 0, 0))
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const w = weekdays[d.getUTCDay()]
  const m = months[d.getUTCMonth()]
  const day = String(d.getUTCDate()).padStart(2, '0')
  const year = String(d.getUTCFullYear()).padStart(4, '0')
  return `${w} ${m} ${day} ${year} 00:00:00 GMT+0000 (Coordinated Universal Time)`
}

const normalizeFoundNegatives = (raw: PersonWithFoundNegatives['foundNegatives'] | undefined | null): FoundNegativesLegacy | null => {
  if (!raw) return null
  if (Array.isArray(raw)) {
    const groups = raw.map((x) => x?.group).filter(Boolean) as string[]
    return { groups: Array.from(new Set(groups)) }
  }
  return raw
}

const DictionaryFoundNegatives: React.FC<DictionaryFoundNegativesProps> = ({ person, hideEmpty }) => {
  const dispatch = useDispatch()
  const token = useSelector(selectToken)

  const name = (person?.name || '').trim()
  const birthday = (person?.birthday || '').trim()

  const iso = birthday.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  const web = birthday.match(/^(\d{2})\.(\d{2})\.(\d{4})$/)

  const birthdayAlt = useMemo(() => {
    if (iso) return toUtcDictionaryBirthdayString(Number(iso[1]), Number(iso[2]), Number(iso[3]))
    if (web) return toUtcDictionaryBirthdayString(Number(web[3]), Number(web[2]), Number(web[1]))
    return ''
  }, [iso, web])

  const entry1 = useDictionaryByEntry({ type: DictionaryEntryType.PERSON, name, birthday })
  const entry2 = useDictionaryByEntry({ type: DictionaryEntryType.PERSON, name, birthday: birthdayAlt })

  useEffect(() => {
    if (!token) return
    if (!name || !birthday) return

    const maybeRequest = (b: string, state?: { status: LoadingStatus } | null) => {
      if (!b) return
      if (state?.status === LoadingStatus.PENDING || state?.status === LoadingStatus.SUCCESS || state?.status === LoadingStatus.FAILED) return
      dispatch(
        getDictionaryLinkedEntriesRequest({
          entry: { type: DictionaryEntryType.PERSON, name, birthday: b },
          referenceTypes: [DictionaryEntryType.PERSON, DictionaryEntryType.PHONE],
        }),
      )
    }

    maybeRequest(birthday, entry1)
    maybeRequest(birthdayAlt, entry2)
  }, [birthday, birthdayAlt, dispatch, entry1, entry2, name, token])

  const entry1Response = entry1?.response
  const entry2Response = entry2?.response

  const foundNegatives = useMemo(() => {
    const responses = [entry1Response, entry2Response].filter(Boolean) as Array<{ entries?: DictionaryEntry[] }>
    const raw = responses.flatMap((r) => (r.entries || []) as PersonWithFoundNegatives[]).find((e) => e?.foundNegatives)?.foundNegatives
    return normalizeFoundNegatives(raw)
  }, [entry1Response, entry2Response])

  if (!foundNegatives || (hideEmpty && !foundNegatives.groups?.length)) return null

  return (
    <div style={{ marginTop: '4px' }}>
      <GrayText text="Негатив из справочника:" />
      <NegativeStatus data={foundNegatives} />
    </div>
  )
}

export default DictionaryFoundNegatives
